import { DropOverActionList, IconButton } from "client/Components";
import { actionComponentFactory, computeActionsToRender } from "client/Components/ActionList/actionsRenderHelpers";
import {
	EmailSelectionAction,
	EmailSelectionActionContext,
	getResponseActions,
	getSelectionActions
 } from "client/Features/Email/Actions/selectionActions";
import { ExchangeEmailId } from "client/Api/Email/common";
import { EmailResponseKind } from "client/Features/Email/EmailPage/EmailPage";
import { MessageFlagState, MessageReadState } from "client/Api/Email/email";
import { generateTrackingId } from "client/Misc/utils";
import { EmailIdWithIsReadStatus } from "client_legacy/Email/emailEvents";
import {
	ActionAndOthers,
	ActionGroup,
	ActionKind
 } from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import {
	Fragment,
	ReactElement,
	RefObject,
	useMemo,
	useRef,
	useState
 } from "react";
import styles from "./ActionButtons.module.less";

export type ActionButtonsProps = {
	onRespondToEmail(
		emailId: ExchangeEmailId,
		kind: EmailResponseKind,
		smtpAddress: string | null
	): void;
	onChangeReadStatus(
		emailIds: readonly ExchangeEmailId[],
		readStatus: MessageReadState,
		smtpAddress: string | null
	): void;
	onChangeFlagStatus(
		emailIds: readonly ExchangeEmailId[],
		flagStatus: MessageFlagState,
		smtpAddress: string | null
	): void;
	onDeleteEmails(emails: readonly EmailIdWithIsReadStatus[], smtpAddress: string | null): void;
	onMoveEmails(emails: readonly EmailIdWithIsReadStatus[], smtpAddress: string | null): void;
	isMobile: boolean;
	onPrint(): void;
	exchangeEmailId: string;
	smtpAddress: string | null;
	isRead: boolean;
	isFlagged: boolean;
	subject: string;
};

export const ActionButtons = (props: ActionButtonsProps): ReactElement => {
	const {
		onRespondToEmail,
		onChangeFlagStatus,
		onChangeReadStatus,
		onDeleteEmails,
		onMoveEmails,
		isMobile,
		onPrint,
		exchangeEmailId,
		smtpAddress,
		isRead,
		isFlagged,
		subject
	} = props;

	const actionsMenuButton: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);
	const [showEmailViewerDropOverList, setShowEmailViewerDropOverList] = useState<boolean>(false);

	const emailViewerActions: readonly EmailSelectionAction[] = useMemo(
		() => getResponseActions(
			onRespondToEmail
		),
		[onRespondToEmail]
	);

	const actions = useMemo(
		(): readonly EmailSelectionAction[] => {
			const selectionActions: readonly EmailSelectionAction[] = getSelectionActions(
				{
					respondToEmail: onRespondToEmail,
					changeFlagStatus: onChangeFlagStatus,
					changeReadStatus: onChangeReadStatus,
					delete: onDeleteEmails,
					move: onMoveEmails
				},
				() => true,
				() => true
			);

			const printAction: EmailSelectionAction = {
				kind: ActionKind.Regular,
				name: "print",
				title: localize("_Print"),
				icon: "print",
				group: printGroup,
				canExecute: () => !window.mobileApp,
				execute: () => onPrint()
			};

			return [...selectionActions, printAction];
		},
		[
			onChangeFlagStatus,
			onChangeReadStatus,
			onDeleteEmails,
			onMoveEmails,
			onRespondToEmail,
			onPrint
		]
	);

	const actionContext = useMemo(
		(): EmailSelectionActionContext => {
			const context: EmailSelectionActionContext = {
				effectiveSelection: [
					{
						emailId: { id: exchangeEmailId },
						subject,
						isRead: isRead,
						isFlagged: isFlagged,
						isDraft: false,
						smtpAddress
					}
				],
				selectedItems: []
			};
			return context;
		},
		[
			exchangeEmailId,
			subject,
			isFlagged,
			isRead,
			smtpAddress
		]
	);

	const actionsToRender: ActionAndOthers<EmailSelectionActionContext>[] = computeActionsToRender(
		actions,
		actionContext
	);

	return (
		<ul className={styles.actionButtons}>
			{emailViewerActions.slice(0, isMobile ? 1 : 3)
				.map((a) => (
					<Fragment key={a.name}>
						{a.icon && (
							<li>
								<IconButton
									icon={a.icon}
									kind="transparent"
									trackingId={a.name}
									onClick={() => a.execute(actionContext)}
									bubbleClick={false}
									shape="round"
								/>
							</li>
						)}

					</Fragment>
				)
			)}
			<li>
				<IconButton
					trackingId="more"
					icon="ellipsis"
					kind="transparent"
					onClick={() => setShowEmailViewerDropOverList(true)}
					ref={actionsMenuButton}
					bubbleClick={false}
					shape="round"
					isActive={showEmailViewerDropOverList}
				/>
				{
					showEmailViewerDropOverList && actionsMenuButton.current && (
						<DropOverActionList
							trackingId={generateTrackingId("more", "__dropover")}
							target={actionsMenuButton.current}
							targetAlignment="br"
							contentAlignment="tr"
							onClose={(): void => setShowEmailViewerDropOverList(false)}
							bubbleClick={false}
							menuTitle={subject}
						>
							{actionsToRender.map((x) => (
								<Fragment key={x.name}>
									{actionComponentFactory(x, actionContext)}
								</Fragment>

							))}
						</DropOverActionList>
					)
				}
			</li>
		</ul>
	);
};
ActionButtons.displayName = "ActionButtons";

const printGroup: ActionGroup = {
	kind: "regular",
	name: "printGroup",
	title: localize("_Print")
};